import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  serverTimestamp,
  runTransaction,
} from 'firebase/firestore';
import { db } from './firebase';
import { auth } from '../firebase';

/** Load all clubs with their events (seasons). */
export async function loadAllClubs() {
  const snap = await getDocs(collection(db, 'clubs'));
  const clubs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  const seasonResults = await Promise.all(
    clubs.map(async (club) => {
      const evSnap = await getDocs(collection(db, 'clubs', club.id, 'events'));
      const seasons = evSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      return { clubId: club.id, seasons };
    }),
  );

  const seasonMap = {};
  for (const r of seasonResults) {
    seasonMap[r.clubId] = r.seasons;
  }

  clubs.sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));
  return { clubs, seasonMap };
}

/** IANA timezones offered in the club editor's picker. Not exhaustive —
 *  the field accepts any zone this browser knows — just the venues we
 *  actually sell into, so the common case is two clicks (ra-h1yt.3). */
export const COMMON_TIMEZONES = [
  'Europe/Dublin',
  'Europe/London',
  'Europe/Lisbon',
  'Europe/Madrid',
  'Europe/Paris',
  'Europe/Amsterdam',
  'Atlantic/Canary',
  'Australia/Sydney',
  'Australia/Brisbane',
  'Australia/Adelaide',
  'Australia/Hobart',
  'Australia/Perth',
  'Pacific/Auckland',
  'America/New_York',
  'America/Los_Angeles',
];

/** True if `tz` is an IANA zone this runtime recognises.
 *
 *  Mirrors functions/race_windows.js::isValidTimezone. Validating on write
 *  keeps a typo ('Australia/Sidney') from reaching Firestore, where it would
 *  read as "no timezone" and silently skip the club's race windows. */
export function isValidTimezone(tz) {
  if (typeof tz !== 'string' || !tz.trim()) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** Migration default for clubs that predate the `timezone` field. Mirrors
 *  functions/race_windows.js::LEGACY_COUNTRY_TIMEZONES — single-timezone
 *  countries only, so this states what the club's zone *is* rather than
 *  substituting the one venue we happen to have most of. */
const LEGACY_COUNTRY_TIMEZONES = {
  IE: 'Europe/Dublin',
  GB: 'Europe/London',
};

/** What the race-window writer will actually use for this club, and why.
 *
 *  Mirrors functions/race_windows.js::resolveClubTimezone so the portal shows
 *  the truth rather than just the stored field: a legacy IE club with no
 *  `timezone` is fine, but an AU club with no `timezone` gets NO race windows
 *  at all — the writer skips it rather than assuming Dublin (ra-h1yt.3).
 *
 *  Returns `{ tz, source }` where source is 'club' | 'country' | null. */
export function resolveClubTimezone(club) {
  if (club && isValidTimezone(club.timezone)) {
    return { tz: club.timezone, source: 'club' };
  }
  const fromCountry = club
    && LEGACY_COUNTRY_TIMEZONES[String(club.country || '').toUpperCase()];
  if (fromCountry) return { tz: fromCountry, source: 'country' };
  return { tz: null, source: null };
}

function assertTimezone(timezone) {
  if (timezone && !isValidTimezone(timezone)) {
    throw new Error(
      `"${timezone}" is not a known IANA timezone. Use a zone name like "Australia/Sydney".`,
    );
  }
}

/** Create a new club document. */
export async function createClub({ id, name, website, lat, lng, timezone }) {
  assertTimezone(timezone);
  const data = {
    name,
    status: 'coming_soon',
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  };
  if (website) data.website = website;
  if (timezone) data.timezone = timezone;
  if (!isNaN(lat) && !isNaN(lng)) data.location = { lat, lng };
  await setDoc(doc(db, 'clubs', id), data);
}

/** Update editable club fields (name, website, location, timezone).
 *
 *  `timezone` is the IANA zone the club's published wall-clock race times are
 *  expressed in — the race-window writer converts start / warning times to UTC
 *  with it (ra-h1yt.3). Clearing it is allowed: IE/GB clubs still resolve from
 *  their country, and for anywhere else the writer skips the club loudly
 *  rather than falling back to Dublin. */
export async function updateClub(clubId, { name, website, lat, lng, timezone }) {
  assertTimezone(timezone);
  const data = { updated_at: serverTimestamp() };
  if (name !== undefined) data.name = name;
  if (website !== undefined) {
    if (website) data.website = website;
    else data.website = deleteField();
  }
  if (timezone !== undefined) {
    if (timezone) data.timezone = timezone;
    else data.timezone = deleteField();
  }
  if (lat !== undefined && lng !== undefined) {
    if (!isNaN(lat) && !isNaN(lng)) data.location = { lat, lng };
    else data.location = deleteField();
  }
  await updateDoc(doc(db, 'clubs', clubId), data);
}

/** Soft-delete a club: sets deleted=true with audit fields. Recoverable
 *  via {@link restoreClub}. App-side list queries filter out deleted=true. */
export async function softDeleteClub(clubId) {
  await updateDoc(doc(db, 'clubs', clubId), {
    deleted: true,
    deletedAt: serverTimestamp(),
    deletedBy: auth.currentUser?.uid || null,
  });
}

/** Restore a soft-deleted club. */
export async function restoreClub(clubId) {
  await updateDoc(doc(db, 'clubs', clubId), {
    deleted: deleteField(),
    deletedAt: deleteField(),
    deletedBy: deleteField(),
  });
}

/** Hard-delete a club document. Reserved for cleanup; admin UI uses
 *  {@link softDeleteClub} instead. */
export async function deleteClub(clubId) {
  await deleteDoc(doc(db, 'clubs', clubId));
}

/** Thrown by {@link createSeason} when a season with the requested id already
 *  exists within the club. Lets the UI offer an explicit overwrite confirm
 *  rather than silently clobbering the existing season's fields. */
export class SeasonExistsError extends Error {
  constructor(clubId, id) {
    super(`A season with ID "${id}" already exists in this club.`);
    this.name = 'SeasonExistsError';
    this.code = 'season-exists';
    this.clubId = clubId;
    this.seasonId = id;
  }
}

/** Create a season (event) under a club.
 *
 *  setDoc replaces the whole document, so reusing an existing id would
 *  silently wipe that season's name/status/dates AND its classes/documents.
 *  Guard against that: refuse by default ({@link SeasonExistsError}) unless
 *  the caller explicitly opts in via `{ overwrite: true }`.
 *
 *  The default path runs the existence check and the write inside a single
 *  transaction so two admins creating the same id concurrently can't both
 *  pass the check and clobber each other (TOCTOU).
 *
 *  Returns the created season object (`{ id, ...data }`) so callers can update
 *  local state optimistically instead of refetching every club. */
export async function createSeason(clubId, { id, name, status, availableDate, expiresDate, access, entitlementId }, { overwrite = false } = {}) {
  const ref = doc(db, 'clubs', clubId, 'events', id);
  // New club seasons default to free (visible to everyone, bundle synced,
  // no entitlement required) so a new club renders in the app with no code
  // deploy. Switch to 'paid' (+ entitlement) or 'interest' as needed.
  const mode = access || 'free';
  const ent = (entitlementId || '').trim();
  // A paid season with no entitlement is unsyncable in the app (it fails the
  // entitlement gate and renders nothing) — refuse to create that dead state.
  if (mode === 'paid' && !ent) {
    throw new Error('Paid seasons require an entitlement ID.');
  }
  const data = {
    name,
    status,
    access: mode,
    documents: [],
    classes: [],
    content_version: 0,
    interest_count: 0,
  };
  if (mode === 'paid') data.entitlement_id = ent;
  if (availableDate) data.available_date = availableDate;
  if (expiresDate) data.expires_date = expiresDate;
  if (overwrite) {
    await setDoc(ref, data); // explicit replacement path
    return { id, ...data };
  }
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (snap.exists()) throw new SeasonExistsError(clubId, id);
    tx.set(ref, data);
  });
  return { id, ...data };
}

/** Update a club's status. */
export async function updateClubStatus(clubId, status) {
  await updateDoc(doc(db, 'clubs', clubId), { status });
}

/** Update a season's status. */
export async function updateSeasonStatus(clubId, seasonId, status) {
  await updateDoc(doc(db, 'clubs', clubId, 'events', seasonId), { status });
}

/** Derive a season's access mode for legacy docs that predate the `access`
 *  field. Mirrors the Flutter EventModel derivation: an entitlement implies
 *  paid, otherwise interest-only. */
export function deriveSeasonAccess(season) {
  if (season && season.access) return season.access;
  return season && season.entitlement_id ? 'paid' : 'interest';
}

/** Update a season's access mode ('interest' | 'free' | 'paid').
 *
 *  Paid requires an entitlement_id and writes it atomically with the mode —
 *  persisting `paid` without one leaves a season the app treats as paid but
 *  cannot sync (it fails the entitlement gate and renders nothing). Free and
 *  interest both clear the entitlement: neither grants access via an entitlement,
 *  and a stale `entitlement_id` would make legacy derivation (and older clients)
 *  read the season as paid. */
export async function updateSeasonAccess(clubId, seasonId, access, { entitlementId } = {}) {
  const ref = doc(db, 'clubs', clubId, 'events', seasonId);
  const data = { access };
  if (access === 'free' || access === 'interest') {
    data.entitlement_id = deleteField();
  } else if (access === 'paid') {
    const ent = (entitlementId || '').trim();
    if (!ent) {
      throw new Error('Paid seasons require an entitlement ID.');
    }
    data.entitlement_id = ent;
  }
  await updateDoc(ref, data);
}

/** Update or remove a season's entitlement_id. */
export async function updateEntitlementId(clubId, seasonId, entitlementId) {
  const ref = doc(db, 'clubs', clubId, 'events', seasonId);
  if (entitlementId) {
    await updateDoc(ref, { entitlement_id: entitlementId });
  } else {
    await updateDoc(ref, { entitlement_id: deleteField() });
  }
}

/** Update a season's available_date and expires_date. */
export async function updateSeasonDates(clubId, seasonId, availableDate, expiresDate) {
  const ref = doc(db, 'clubs', clubId, 'events', seasonId);
  const data = {};
  if (availableDate) {
    data.available_date = availableDate;
  } else {
    data.available_date = deleteField();
  }
  if (expiresDate) {
    data.expires_date = expiresDate;
  } else {
    data.expires_date = deleteField();
  }
  await updateDoc(ref, data);
}

/** Add (or upsert by id) a class to a season.
 *
 *  Runs the read-modify-write in a transaction and dedups by class id. The old
 *  arrayUnion path dedup'd by the whole {id,name} object, so re-adding an
 *  existing id with a changed name silently wrote a duplicate id server-side.
 *  Here an existing id is updated in place (last-write-wins on the name) rather
 *  than duplicated. Returns the canonical classes array after the write so the
 *  caller can render server truth instead of guessing locally. */
export async function addClass(clubId, seasonId, classId, className) {
  const ref = doc(db, 'clubs', clubId, 'events', seasonId);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const existing = (snap.exists() && snap.data().classes) || [];
    const next = existing.some((c) => c.id === classId)
      ? existing.map((c) => (c.id === classId ? { id: classId, name: className } : c))
      : [...existing, { id: classId, name: className }];
    tx.update(ref, { classes: next });
    return next;
  });
}

/** Remove a class from a season. */
export async function removeClass(clubId, seasonId, classId) {
  const ref = doc(db, 'clubs', clubId, 'events', seasonId);
  const snap = await getDoc(ref);
  const classes = (snap.data().classes || []).filter((c) => c.id !== classId);
  await updateDoc(ref, { classes });
}

/** Add classes to a season, merging by id (dedup, preserves existing order).
 *  Used to propagate fleet_config classes the app does not yet offer for
 *  registration into a season. Only appends ids not already present, so it is
 *  safe to call repeatedly. Returns the ids actually added.
 *
 *  Runs the read-merge-write in a single transaction so a concurrent
 *  add/remove of a class between the read and the write can't be lost (the
 *  merge re-reads the current array at commit time). */
export async function addClassesToSeason(clubId, seasonId, newClasses) {
  const ref = doc(db, 'clubs', clubId, 'events', seasonId);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return [];
    const existing = snap.data().classes || [];
    const seen = new Set(existing.map((c) => c.id));
    const added = [];
    for (const c of newClasses) {
      if (!c || !c.id || seen.has(c.id)) continue;
      seen.add(c.id);
      added.push({ id: c.id, name: c.name || c.id });
    }
    if (added.length === 0) return [];
    tx.update(ref, { classes: [...existing, ...added] });
    return added.map((c) => c.id);
  });
}

/** Reorder classes in a single season to match fleet_config class order.
 *  Only reorders existing classes — doesn't add or remove.
 *  Returns true if the season was updated. */
export async function reorderSeasonClasses(clubId, seasonId, orderedClasses) {
  const orderMap = new Map(orderedClasses.map((c, i) => [c.id, i]));
  const ref = doc(db, 'clubs', clubId, 'events', seasonId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return false;
  const seasonClasses = snap.data().classes || [];
  if (seasonClasses.length < 2) return false;
  const sorted = [...seasonClasses].sort((a, b) => {
    const ai = orderMap.has(a.id) ? orderMap.get(a.id) : Infinity;
    const bi = orderMap.has(b.id) ? orderMap.get(b.id) : Infinity;
    return ai - bi;
  });
  // Only update if order actually changed
  const changed = seasonClasses.some((c, i) => c.id !== sorted[i].id);
  if (changed) {
    await updateDoc(ref, { classes: sorted });
    return true;
  }
  return false;
}
